import { useCallback, useEffect, useState } from "react";

import type {
    AttributionPages,
    AttributionScope,
    PageSortKey,
    PageTypeFilter,
    SortDir
} from "./attributionReportApi";
import { fetchAttributionPages } from "./attributionReportApi";

export interface PagesQuery {
    startDate?: string;
    endDate?: string;
    attributionScope: AttributionScope;
    sortBy: PageSortKey;
    sortDir: SortDir;
    page: number;
    pageSize: number;
    pageType?: PageTypeFilter;
    vendor?: string;
}

export interface AttributionPagesState {
    data: AttributionPages | null;
    loading: boolean;
    /** Only ever set from a 400 — degraded states are normal 200 bodies. */
    error: string | null;
    refetch: () => void;
}

/**
 * Page performance rows.
 *
 * Sorting, paging and filtering are all server-side, so every one of them is a
 * dependency here and triggers a refetch. Sorting `rows` in the client would only
 * reorder the current page and quietly misrepresent the whole set.
 */
export function useAttributionPages(query: PagesQuery, enabled: boolean): AttributionPagesState {
    const { startDate, endDate, attributionScope, sortBy, sortDir, page, pageSize, pageType, vendor } = query;

    const [data, setData] = useState<AttributionPages | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nonce, setNonce] = useState(0);

    const refetch = useCallback(() => setNonce((n) => n + 1), []);

    useEffect(() => {
        if (!enabled) return;
        let cancelled = false;
        setLoading(true);
        fetchAttributionPages({
            startDate,
            endDate,
            attributionScope,
            sortBy,
            sortDir,
            page,
            pageSize,
            pageType,
            vendor
        }).then((res) => {
            if (cancelled) return;
            setLoading(false);
            if (res.ok) {
                setData(res.data);
                return;
            }
            if (res.badRequest) setError(res.message);
        });
        return () => {
            cancelled = true;
        };
    }, [enabled, startDate, endDate, attributionScope, sortBy, sortDir, page, pageSize, pageType, vendor, nonce]);

    return { data, loading, error, refetch };
}
